import type { AppRouter } from '@app/api'
import { BOARD_AHEAD_HOURS, type Board, type BoardMoment } from '@app/shared'
import type { inferRouterOutputs } from '@trpc/server'

export type Leading = inferRouterOutputs<AppRouter>['occasion']['leading']
export type Ahead = inferRouterOutputs<AppRouter>['board']['ahead']
export const BOARD_REREAD_MS = 60_000

/** Prefer held time-sensitive data while a new leading answer is still loading. */
export function shownLeading(
  live: Leading | null,
  held: Leading | null,
  state: { error: boolean; placeholder: boolean },
): Leading | null {
  if (held && (state.error || state.placeholder || !live)) return held
  return state.error ? null : live
}

/**
 * The held answer for `now`: the last segment begun by then, inside the
 * hours the read covers. Outside them nothing is held, rather than an answer
 * for some other day.
 */
export function covering(ahead: Ahead | null, now: number): Leading | null {
  if (!ahead?.segments.length) return null
  const starts = ahead.segments.map((segment) => Date.parse(segment.from))
  const first = starts[0]
  if (now < first || now >= first + BOARD_AHEAD_HOURS * 3_600_000) return null
  let found = ahead.segments[0]
  for (const [index, segment] of ahead.segments.entries()) {
    if (starts[index] > now) break
    found = segment
  }
  return found.leading
}

/** The held band's marker follows the screen clock; it is not a resolved time. */
export function markNow(
  leading: Leading,
  now: number,
  timeZone: string,
): Leading {
  if (!leading.band) return leading
  return {
    ...leading,
    band: {
      ...leading.band,
      now: {
        at: new Date(now).toISOString(),
        time: new Intl.DateTimeFormat('en-US', {
          timeZone,
          hour: 'numeric',
          minute: '2-digit',
        }).format(now),
      },
    },
  }
}

/**
 * What a board's playlists are decided against, read from the one public
 * answer about where the shul stands at a moment.
 */
export function momentOf(leading: Leading): BoardMoment {
  return {
    date: leading.today.date,
    occasionKey: leading.moment.occasionKey,
    rest: leading.moment.rest,
  }
}

/**
 * The board a screen shows when its link carries none: every widget once, on
 * the layout the product leads with, so the address alone shows a board.
 */
export const SAMPLE_BOARD: Board = {
  layout: {
    ratio: '16:9',
    cells: [
      { id: 'top-1', x: 0, y: 0, w: 9, h: 4 },
      { id: 'top-3', x: 9, y: 0, w: 9, h: 4 },
      { id: 'clock', x: 18, y: 0, w: 6, h: 4 },
      { id: 'main-1a', x: 0, y: 4, w: 8, h: 10 },
      { id: 'main-1b', x: 8, y: 4, w: 8, h: 4 },
      { id: 'main-2', x: 8, y: 8, w: 8, h: 6 },
      { id: 'main-3', x: 16, y: 4, w: 8, h: 4 },
      { id: 'bottom-1', x: 16, y: 8, w: 8, h: 6 },
    ],
  },
  zones: {
    clock: [
      {
        widget: { kind: 'clock', face: 'digital' },
        seconds: 30,
        window: null,
        shabbosSafe: true,
        takeover: false,
      },
    ],
    'top-1': [
      {
        widget: { kind: 'announcement', title: 'Welcome', body: '' },
        seconds: 30,
        window: null,
        shabbosSafe: true,
        takeover: false,
      },
    ],
    'top-3': [
      {
        widget: {
          kind: 'announcement',
          title: 'Kiddush this Shabbos',
          body: 'Sponsored by the Levine family',
        },
        seconds: 30,
        window: null,
        shabbosSafe: false,
        takeover: false,
      },
    ],
    'main-1a': [
      {
        widget: { kind: 'zmanim' },
        seconds: 30,
        window: null,
        shabbosSafe: true,
        takeover: false,
      },
    ],
    'main-1b': [
      {
        widget: {
          kind: 'announcement',
          title: 'Daf Yomi',
          body: 'After Maariv, in the beis medrash',
        },
        seconds: 15,
        window: null,
        shabbosSafe: true,
        takeover: false,
      },
      {
        widget: {
          kind: 'announcement',
          title: 'Mazel tov',
          body: 'To the Kaplan family on the birth of a grandson',
        },
        seconds: 15,
        window: null,
        shabbosSafe: true,
        takeover: false,
      },
    ],
    'main-2': [
      {
        widget: { kind: 'schedule' },
        seconds: 30,
        window: null,
        shabbosSafe: true,
        takeover: false,
      },
    ],
    'main-3': [
      {
        widget: {
          kind: 'announcement',
          title: 'Building campaign',
          body: 'Dedications are still open — ask the office',
        },
        seconds: 30,
        window: null,
        shabbosSafe: false,
        takeover: false,
      },
    ],
    'bottom-1': [
      {
        widget: { kind: 'zmanim' },
        seconds: 30,
        window: null,
        shabbosSafe: true,
        takeover: false,
      },
    ],
  },
  standIns: [],
}
